// =====================================================
// MEDICAL ACT SEARCH - v3
// · Layout dos columnas: sidebar filtros + panel resultados
// · Paginación cliente (PAGE_SIZE items, panel fill height)
// · Grupos de tratamiento inteligentes: solo muestra los
//   grupos presentes en los resultados actuales
// =====================================================
import { useState, useCallback, useEffect, useMemo } from 'react';
import { budgetsApi } from '../../api/budgets';
import { specialtiesApi } from '../../api/specialties';
import { subspecialtiesApi } from '../../api/subspecialties';
import type { BudgetMedicalActInfo } from '../../types/budget';

interface SelectOption {
  id: string;
  code: string;
  name: string;
}

interface MedicalActSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (act: BudgetMedicalActInfo) => void;
  defaultSocietyId?: string | null;
}

const PAGE_SIZE = 10;

const PRICE_SOURCE_LABEL: Record<string, string> = {
  site: 'Precio centro',
  act: 'Precio base',
  tariff: 'Tarifa',
  override: 'Manual',
  none: 'Sin precio',
};

export function MedicalActSearch({
  isOpen,
  onClose,
  onSelect,
}: MedicalActSearchProps) {
  // ── Búsqueda y resultados ─────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  // allResults: respuesta sin filtro de grupo (base para derivar grupos disponibles)
  const [allResults, setAllResults] = useState<BudgetMedicalActInfo[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Filtros API (especialidad + subespecialidad) ──
  const [specialtyId, setSpecialtyId] = useState('');
  const [subspecialtyId, setSubspecialtyId] = useState('');

  // ── Filtro cliente (grupo de tratamiento) ─────────
  const [treatmentGroupId, setTreatmentGroupId] = useState('');

  // ── Paginación ────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ── Datos de los selects ──────────────────────────
  const [specialties, setSpecialties] = useState<SelectOption[]>([]);
  const [subspecialties, setSubspecialties] = useState<SelectOption[]>([]);
  const [loadingSubspecialties, setLoadingSubspecialties] = useState(false);

  // ── Carga inicial ─────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    specialtiesApi.getForSelect().then(setSpecialties).catch(console.error);
  }, [isOpen]);

  // ── Subespecialidades en cascada ──────────────────
  useEffect(() => {
    if (!specialtyId) {
      setSubspecialties([]);
      setSubspecialtyId('');
      return;
    }
    setLoadingSubspecialties(true);
    setSubspecialtyId('');
    subspecialtiesApi
      .getForSelect(specialtyId)
      .then(setSubspecialties)
      .catch(console.error)
      .finally(() => setLoadingSubspecialties(false));
  }, [specialtyId]);

  // ── Resetear página y grupo al cambiar filtros API ─
  useEffect(() => {
    setCurrentPage(1);
    setTreatmentGroupId('');
  }, [searchQuery, specialtyId, subspecialtyId]);

  // ── Búsqueda contra API (sin treatmentGroupId) ────
  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    setError(null);
    try {
      const data = await budgetsApi.searchMedicalActs({
        search: searchQuery.trim() || undefined,
        specialtyId: specialtyId || undefined,
        subspecialtyId: subspecialtyId || undefined,
        limit: 200,
      });
      setAllResults(data);
    } catch (err: any) {
      setError(err.message || 'Error al buscar actos médicos');
      setAllResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, specialtyId, subspecialtyId]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(handleSearch, 300);
    return () => clearTimeout(t);
  }, [searchQuery, specialtyId, subspecialtyId, isOpen]);

  // ── Grupos disponibles (derivados de allResults) ──
  // Solo se muestran los grupos que tienen actos en la lista actual.
  const availableTreatmentGroups = useMemo((): SelectOption[] => {
    const map = new Map<string, SelectOption>();
    for (const act of allResults) {
      for (const g of act.treatmentGroups) {
        if (!map.has(g.id)) {
          map.set(g.id, { id: g.id, code: g.id, name: g.name });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allResults]);

  // ── Resultados filtrados (cliente, por grupo) ─────
  const filteredResults = useMemo(() => {
    if (!treatmentGroupId) return allResults;
    return allResults.filter(act =>
      act.treatmentGroups.some(g => g.id === treatmentGroupId)
    );
  }, [allResults, treatmentGroupId]);

  // ── Paginación ────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filteredResults.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageResults = filteredResults.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  // Resetear página cuando cambia el grupo de tratamiento
  useEffect(() => {
    setCurrentPage(1);
  }, [treatmentGroupId]);

  // ── Números de página visibles ────────────────────
  const pageNumbers = useMemo((): (number | '…')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | '…')[] = [1];
    if (safePage > 3) pages.push('…');
    for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) {
      pages.push(i);
    }
    if (safePage < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  }, [totalPages, safePage]);

  // ── Handlers ──────────────────────────────────────
  const handleClose = () => {
    setSearchQuery('');
    setAllResults([]);
    setSpecialtyId('');
    setSubspecialtyId('');
    setTreatmentGroupId('');
    setError(null);
    setCurrentPage(1);
    onClose();
  };

  const handleClearFilters = () => {
    setSpecialtyId('');
    setSubspecialtyId('');
    setTreatmentGroupId('');
    setSearchQuery('');
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(price);

  const hasActiveFilters = !!(specialtyId || subspecialtyId || treatmentGroupId || searchQuery.trim());
  const activeFilterCount = [specialtyId, subspecialtyId, treatmentGroupId].filter(Boolean).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal — altura dinámica: ocupa hasta 90vh y el contenido se adapta */}
      <div className="relative z-10 w-full max-w-6xl bg-white rounded-2xl shadow-2xl flex flex-col"
           style={{ height: 'min(90vh, 860px)' }}>

        {/* ── Header ───────────────────────────────── */}
        <div className="flex items-start justify-between px-8 py-5 bg-white border-b border-slate-100 shrink-0 rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-primary tracking-tight">
              Buscar acto médico
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Busca y añade actos al presupuesto
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
        </div>

        {/* ── Cuerpo: dos columnas, altura flexible ─── */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Sidebar filtros (300px fijo) ─────────── */}
          <aside className="w-[300px] shrink-0 border-r border-slate-100 bg-slate-50 flex flex-col overflow-y-auto custom-scrollbar rounded-bl-2xl">
            <div className="p-6 space-y-6">

              {/* Buscador */}
              <div className="space-y-2">
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-400">
                  Buscador
                </label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-primary text-xl select-none">
                    search
                  </span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Nombre o código..."
                    className="w-full pl-10 pr-4 h-12 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all shadow-sm"
                    autoFocus
                  />
                </div>
              </div>

              <div className="border-t border-slate-200" />

              {/* Filtros */}
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                    Filtros
                  </span>
                  {activeFilterCount > 0 && (
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded-full">
                      {activeFilterCount} activo{activeFilterCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Especialidad */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-600">
                    Especialidad
                  </label>
                  <div className="relative">
                    <select
                      value={specialtyId}
                      onChange={(e) => setSpecialtyId(e.target.value)}
                      className="w-full h-11 pl-3 pr-9 bg-white border border-slate-200 rounded-xl appearance-none text-sm font-medium text-slate-700 focus:ring-2 focus:ring-primary focus:border-primary transition-all shadow-sm cursor-pointer"
                    >
                      <option value="">Todas las especialidades</option>
                      {specialties.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xl select-none">
                      expand_more
                    </span>
                  </div>
                </div>

                {/* Subespecialidad — en cascada */}
                <div className="space-y-2">
                  <label className={`block text-xs font-semibold transition-colors ${specialtyId ? 'text-slate-600' : 'text-slate-400'}`}>
                    Subespecialidad
                    {!specialtyId && (
                      <span className="ml-1 font-normal text-slate-400">(elige especialidad)</span>
                    )}
                  </label>
                  <div className="relative">
                    <select
                      value={subspecialtyId}
                      onChange={(e) => setSubspecialtyId(e.target.value)}
                      disabled={!specialtyId || loadingSubspecialties}
                      className="w-full h-11 pl-3 pr-9 bg-white border border-slate-200 rounded-xl appearance-none text-sm font-medium text-slate-700 focus:ring-2 focus:ring-primary focus:border-primary transition-all shadow-sm cursor-pointer disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {loadingSubspecialties ? 'Cargando...' : 'Todas las subespecialidades'}
                      </option>
                      {subspecialties.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <span className={`material-symbols-outlined absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-xl select-none ${specialtyId ? 'text-slate-400' : 'text-slate-300'}`}>
                      {loadingSubspecialties ? 'progress_activity' : 'expand_more'}
                    </span>
                  </div>
                </div>

                {/* Grupo de tratamiento — inteligente, solo grupos con actos */}
                <div className="space-y-2">
                  <label className={`block text-xs font-semibold transition-colors ${
                    availableTreatmentGroups.length > 0 ? 'text-slate-600' : 'text-slate-400'
                  }`}>
                    Grupo de tratamiento
                    {availableTreatmentGroups.length > 0 && (
                      <span className="ml-1.5 text-[10px] font-bold text-slate-400 normal-case">
                        ({availableTreatmentGroups.length})
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <select
                      value={treatmentGroupId}
                      onChange={(e) => setTreatmentGroupId(e.target.value)}
                      disabled={availableTreatmentGroups.length === 0 || isSearching}
                      className="w-full h-11 pl-3 pr-9 bg-white border border-slate-200 rounded-xl appearance-none text-sm font-medium text-slate-700 focus:ring-2 focus:ring-primary focus:border-primary transition-all shadow-sm cursor-pointer disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                    >
                      <option value="">Todos los grupos</option>
                      {availableTreatmentGroups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    <span className={`material-symbols-outlined absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-xl select-none ${
                      availableTreatmentGroups.length > 0 ? 'text-slate-400' : 'text-slate-300'
                    }`}>
                      expand_more
                    </span>
                  </div>
                </div>

                {/* Limpiar filtros */}
                {hasActiveFilters && (
                  <button
                    onClick={handleClearFilters}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-slate-500 border border-slate-200 bg-white rounded-xl hover:bg-slate-100 hover:text-slate-700 transition-colors"
                  >
                    <span className="material-symbols-outlined text-base">filter_alt_off</span>
                    Limpiar filtros
                  </button>
                )}
              </div>
            </div>
          </aside>

          {/* ── Panel resultados (flex-1) ─────────────── */}
          <main className="flex-1 flex flex-col bg-white min-h-0 rounded-br-2xl">

            {/* Lista — ocupa todo el espacio disponible */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-5 space-y-2.5">

              {/* Error */}
              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <span className="material-symbols-outlined text-red-500 text-xl shrink-0">error</span>
                  {error}
                </div>
              )}

              {/* Buscando */}
              {isSearching ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-slate-400">
                  <span className="material-symbols-outlined text-5xl animate-spin mb-3 text-primary/40">
                    progress_activity
                  </span>
                  <p className="text-sm font-medium">Buscando actos médicos...</p>
                </div>

              ) : pageResults.length > 0 ? (
                pageResults.map((act) => (
                  <div
                    key={act.id}
                    className="flex items-center justify-between p-5 bg-white rounded-xl border border-slate-100 shadow-sm hover:border-primary/30 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => onSelect(act)}
                  >
                    {/* Info */}
                    <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="px-2 py-0.5 rounded-md bg-slate-100 text-[10px] font-bold text-slate-500 tracking-widest uppercase shrink-0">
                          {act.code}
                        </span>
                        <span className="font-bold text-base text-slate-800 truncate">
                          {act.name}
                        </span>
                        {act.requiresOdontogram && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold rounded-full shrink-0">
                            <span className="material-symbols-outlined text-sm">dentistry</span>
                            Odontograma
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {act.specialties.map((s) => (
                          <span key={s.id} className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold">
                            {s.name}
                          </span>
                        ))}
                        {act.treatmentGroups.length > 0 && (
                          <span className="text-slate-400 text-xs">
                            • {act.treatmentGroups.map((g) => g.name).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Precio + botón */}
                    <div className="flex items-center gap-6 pl-6 shrink-0">
                      <div className="text-right">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                          {PRICE_SOURCE_LABEL[act.priceSource] ?? 'Precio'}
                        </p>
                        <p className="text-xl font-bold text-primary">
                          {formatPrice(act.calculatedPrice)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); onSelect(act); }}
                        className="flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 active:scale-95 transition-all shadow-sm shadow-primary/20 hover:shadow-md"
                      >
                        <span className="material-symbols-outlined text-xl">add_circle</span>
                        Añadir
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-slate-400">
                  <span className="material-symbols-outlined text-6xl mb-4 opacity-20">search_off</span>
                  <p className="font-semibold text-slate-500">No se encontraron actos médicos</p>
                  <p className="text-sm mt-1">
                    {hasActiveFilters
                      ? 'Prueba a ajustar o limpiar los filtros'
                      : 'Escribe un código o nombre para buscar'}
                  </p>
                </div>
              )}
            </div>

            {/* ── Footer: contador + paginación + cerrar ─ */}
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-4 shrink-0 rounded-br-2xl">

              {/* Contador */}
              <div className="flex items-center gap-2 text-slate-500 shrink-0">
                <span className="material-symbols-outlined text-lg text-slate-400">list_alt</span>
                <span className="text-sm font-semibold">
                  {isSearching ? 'Buscando...' : `${filteredResults.length} resultado${filteredResults.length !== 1 ? 's' : ''}`}
                </span>
              </div>

              {/* Paginación — solo si hay más de una página */}
              {!isSearching && totalPages > 1 && (
                <nav className="flex items-center gap-1">
                  {/* Anterior */}
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-xl">chevron_left</span>
                  </button>

                  {/* Números */}
                  {pageNumbers.map((p, i) =>
                    p === '…' ? (
                      <span key={`ellipsis-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm select-none">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg font-semibold text-sm transition-colors ${
                          p === safePage
                            ? 'bg-primary text-white shadow-sm'
                            : 'text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                  {/* Siguiente */}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-xl">chevron_right</span>
                  </button>
                </nav>
              )}

              {/* Cerrar */}
              <button
                onClick={handleClose}
                className="px-5 py-2 bg-white text-slate-600 border border-slate-200 rounded-xl font-semibold text-sm hover:bg-slate-100 transition-colors shrink-0"
              >
                Cerrar
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
